'use client';

import { Link, Outlet, createFileRoute, useNavigate, useRouterState } from '@tanstack/react-router';
import { FileUp, Plus, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { useControls } from '@/features/controls/api/use-controls';
import { DEFAULT_CONTROL_FILTERS } from '@/features/controls/types';
import type { Control, ControlFilters } from '@/features/controls/types';
import { useMembers } from '@/features/team/api/use-members';
import type { OrganizationMember } from '@/features/team/types';
import {
  useCreateTrainingRecord,
  useTrainingRecords,
  useUpdateTrainingRecord,
} from '@/features/workforce-readiness/api/use-training-records';
import {
  type EmployeeCompletionStatus,
  type TrainingRecord,
  type TrainingRecordStatus,
} from '@/features/workforce-readiness/data';
import {
  buildTrainingRecordInput,
  mapTrainingRecordFromApi,
} from '@/features/workforce-readiness/mappers';
import { useActiveOrganizationId } from '@/hooks/use-active-org-id';
import { useOrganizations } from '@/hooks/use-organizations';
import i18n from '@/lib/i18n';

type TrainingStatusFilter = 'all' | TrainingRecordStatus;

interface ParticipantProgressFormValue {
  completionStatus: EmployeeCompletionStatus;
  completionDate: string | null;
  evidenceLabel: string | null;
}

interface TrainingFormState {
  trainingName: string;
  trainingType: string;
  description: string;
  status: TrainingRecordStatus;
  date: string;
  selectedParticipantIds: string[];
  participantProgress: Record<string, ParticipantProgressFormValue>;
  selectedControlIds: string[];
}

const EMPTY_FORM_STATE: TrainingFormState = {
  trainingName: '',
  trainingType: '',
  description: '',
  status: 'planned',
  date: '',
  selectedParticipantIds: [],
  participantProgress: {},
  selectedControlIds: [],
};

export const Route = createFileRoute('/_authenticated/workforce-readiness/training-register')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.workforceReadinessTrainingRegister'),
  }),
  component: TrainingRegisterPage,
});

function TrainingRegisterPage() {
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });

  if (pathname !== '/workforce-readiness/training-register') {
    return <Outlet />;
  }

  return <TrainingRegisterContent />;
}

function TrainingRegisterContent() {
  const { t } = useTranslation('common');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<TrainingStatusFilter>('all');
  const [typeFilter, setTypeFilter] = useState('all');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTrainingId, setEditingTrainingId] = useState<string | null>(null);
  const [formState, setFormState] = useState<TrainingFormState>(EMPTY_FORM_STATE);
  const [formError, setFormError] = useState<string | null>(null);
  const [participantSearch, setParticipantSearch] = useState('');
  const [controlSearch, setControlSearch] = useState('');

  const navigate = useNavigate();
  const { organizations, isLoading: orgsLoading } = useOrganizations();
  const { organizationId: resolvedOrgId } = useActiveOrganizationId();
  const activeOrg = organizations.find((org) => org.isActive);
  const effectiveOrgId = activeOrg?.id ?? resolvedOrgId;
  const canManageAssignments = Boolean(effectiveOrgId);
  const trainingQuery = useTrainingRecords({
    status: statusFilter === 'all' ? undefined : statusFilter,
    search: search.trim() || undefined,
  });
  const createTrainingMutation = useCreateTrainingRecord();
  const updateTrainingMutation = useUpdateTrainingRecord();

  const trainingRecords = useMemo(
    () => trainingQuery.records?.items.map(mapTrainingRecordFromApi) ?? [],
    [trainingQuery.records?.items]
  );

  const membersQueryOptions = useMemo(
    () => ({
      orgId: effectiveOrgId ?? '',
      page: 1,
      pageSize: 300,
      sortBy: 'name' as const,
      sortOrder: 'asc' as const,
      enabled: Boolean(effectiveOrgId),
    }),
    [effectiveOrgId]
  );

  const { data: membersData, isLoading: membersLoading } = useMembers(membersQueryOptions);
  const availableMembers = useMemo(() => membersData?.members ?? [], [membersData?.members]);

  const controlsFilters = useMemo<ControlFilters>(
    () => ({
      ...DEFAULT_CONTROL_FILTERS,
      sortBy: 'title',
      sortOrder: 'asc',
    }),
    []
  );

  const { controls: fetchedControls, isLoading: controlsLoading } = useControls({
    filters: controlsFilters,
    page: 1,
    pageSize: 300,
    enabled: Boolean(effectiveOrgId),
  });

  const availableControls = fetchedControls;

  const membersByUserId = useMemo(
    () => new Map(availableMembers.map((member) => [member.userId, member])),
    [availableMembers]
  );

  const controlsById = useMemo(
    () => new Map(availableControls.map((control) => [control.id, control])),
    [availableControls]
  );

  const trainingTypeOptions = useMemo(() => {
    const uniqueTypes = new Set(trainingRecords.map((training) => training.trainingType));
    const currentFormType = formState.trainingType.trim();

    if (currentFormType) {
      uniqueTypes.add(currentFormType);
    }

    return ['all', ...Array.from(uniqueTypes)];
  }, [formState.trainingType, trainingRecords]);

  const filteredRecords = useMemo(() => {
    return trainingRecords.filter((training) => {
      const matchesType = typeFilter === 'all' || training.trainingType === typeFilter;

      return matchesType;
    });
  }, [trainingRecords, typeFilter]);

  const filteredMembersForDialog = useMemo(() => {
    const query = participantSearch.trim().toLowerCase();
    if (!query) {
      return availableMembers;
    }

    return availableMembers.filter((member) => {
      const displayName = getMemberDisplayName(member).toLowerCase();
      const email = member.email.toLowerCase();
      return displayName.includes(query) || email.includes(query);
    });
  }, [availableMembers, participantSearch]);

  const filteredControlsForDialog = useMemo(() => {
    const query = controlSearch.trim().toLowerCase();
    if (!query) {
      return availableControls;
    }

    return availableControls.filter((control) => {
      const title = control.title.toLowerCase();
      const code = control.controlCode.toLowerCase();
      return title.includes(query) || code.includes(query);
    });
  }, [availableControls, controlSearch]);

  const openCreateDialog = () => {
    setEditingTrainingId(null);
    setFormState(EMPTY_FORM_STATE);
    setParticipantSearch('');
    setControlSearch('');
    setFormError(null);
    setDialogOpen(true);
  };

  const openEditDialog = (training: TrainingRecord) => {
    setEditingTrainingId(training.id);
    setFormState({
      trainingName: training.trainingName,
      trainingType: training.trainingType,
      description: training.description,
      status: training.status,
      date: training.date,
      selectedParticipantIds: training.assignedEmployees.map((e) => e.userId),
      participantProgress: Object.fromEntries(
        training.assignedEmployees.map((e) => [
          e.userId,
          {
            completionStatus: e.completionStatus,
            completionDate: e.completionDate,
            evidenceLabel: e.evidenceLabel,
          },
        ])
      ),
      selectedControlIds: [...training.linkedControlIds],
    });
    setParticipantSearch('');
    setControlSearch('');
    setFormError(null);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingTrainingId(null);
    setFormState(EMPTY_FORM_STATE);
    setFormError(null);
    setParticipantSearch('');
    setControlSearch('');
  };

  const upsertParticipantSelection = (userId: string, checked: boolean) => {
    setFormState((previousState) => {
      const selectedParticipantIds = checked
        ? uniqueValues([...previousState.selectedParticipantIds, userId])
        : previousState.selectedParticipantIds.filter((id) => id !== userId);

      if (checked) {
        const participantProgress = {
          ...previousState.participantProgress,
        };
        participantProgress[userId] ??= {
          completionStatus: 'not_started',
          completionDate: null,
          evidenceLabel: null,
        };

        return {
          ...previousState,
          selectedParticipantIds,
          participantProgress,
        };
      }

      const participantProgress = Object.fromEntries(
        Object.entries(previousState.participantProgress).filter(([id]) => id !== userId)
      ) as Record<string, ParticipantProgressFormValue>;

      return {
        ...previousState,
        selectedParticipantIds,
        participantProgress,
      };
    });
  };

  const upsertControlSelection = (controlId: string, checked: boolean) => {
    setFormState((previousState) => {
      const selectedControlIds = checked
        ? uniqueValues([...previousState.selectedControlIds, controlId])
        : previousState.selectedControlIds.filter((id) => id !== controlId);

      return {
        ...previousState,
        selectedControlIds,
      };
    });
  };

  const updateParticipantProgress = (
    userId: string,
    patch: Partial<ParticipantProgressFormValue>
  ) => {
    setFormState((previousState) => {
      const currentValue = previousState.participantProgress[userId] ?? {
        completionStatus: 'not_started',
        completionDate: null,
        evidenceLabel: null,
      };

      return {
        ...previousState,
        participantProgress: {
          ...previousState.participantProgress,
          [userId]: {
            ...currentValue,
            ...patch,
          },
        },
      };
    });
  };

  const saveTraining = async () => {
    const normalizedName = formState.trainingName.trim();
    const normalizedType = formState.trainingType.trim();

    if (!normalizedName) {
      setFormError(t('workforceReadiness.training.validationNameRequired'));
      return;
    }

    if (!normalizedType) {
      setFormError(t('workforceReadiness.training.validationTypeRequired'));
      return;
    }

    if (!formState.date) {
      setFormError(t('workforceReadiness.training.validationDateRequired'));
      return;
    }

    setFormError(null);
    const baseTraining: TrainingRecord =
      (editingTrainingId
        ? trainingRecords.find((record) => record.id === editingTrainingId)
        : null) ?? {
        id: '',
        trainingName: '',
        trainingType: '',
        description: '',
        status: 'planned',
        date: '',
        assignedEmployees: [],
        linkedControlIds: [],
        agendaItems: [],
        evidenceItems: [],
        resourceLinks: [],
        evidenceCount: 0,
      };

    const nextTraining: TrainingRecord = {
      ...baseTraining,
      trainingName: normalizedName,
      trainingType: normalizedType,
      description: formState.description.trim(),
      status: formState.status,
      date: formState.date,
      assignedEmployees: formState.selectedParticipantIds.map((userId) => {
        const progress = formState.participantProgress[userId] ?? {
          completionStatus: 'not_started' as const,
          completionDate: null,
          evidenceLabel: null,
        };

        return {
          userId,
          completionStatus: progress.completionStatus,
          completionDate: progress.completionDate,
          evidenceLabel: progress.evidenceLabel,
        };
      }),
      linkedControlIds: formState.selectedControlIds,
    };

    try {
      if (editingTrainingId) {
        await updateTrainingMutation.updateRecord({
          recordId: editingTrainingId,
          data: buildTrainingRecordInput(nextTraining),
        });
      } else {
        await createTrainingMutation.createRecord(buildTrainingRecordInput(nextTraining));
      }
      closeDialog();
    } catch (error) {
      setFormError(error instanceof Error ? error.message : t('workforceReadiness.training.failedToSave'));
    }
  };

  if (orgsLoading || (trainingQuery.isLoading && trainingRecords.length === 0)) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-[1300px]">
          <div className="animate-pulse space-y-4">
            <div className="h-8 w-56 rounded bg-muted" />
            <div className="h-10 rounded bg-muted" />
            <div className="h-80 rounded bg-muted" />
          </div>
        </div>
      </div>
    );
  }

  if (trainingQuery.isError) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-[1300px]">
          <Card>
            <CardContent className="py-6 text-sm text-destructive">
              {trainingQuery.error?.message ?? t('workforceReadiness.training.failedToLoad')}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="mx-auto max-w-[1300px] space-y-6">
        <header className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h1 className="text-3xl font-semibold text-foreground">{t('workforceReadiness.training.pageTitle')}</h1>
            <p className="mt-1 text-muted-foreground">
              {t('workforceReadiness.training.pageDescription')}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" asChild>
              <Link to="/settings/team">{t('workforceReadiness.training.users')}</Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to="/audit/evidence-library">{t('workforceReadiness.training.evidence')}</Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to="/controls">{t('workforceReadiness.training.complianceControls')}</Link>
            </Button>
            <Button onClick={openCreateDialog}>
              <Plus className="mr-2 h-4 w-4" />
              {t('workforceReadiness.training.addTraining')}
            </Button>
          </div>
        </header>

        {!canManageAssignments ? (
          <Card className="border-dashed">
            <CardContent className="flex flex-col gap-2 py-4 md:flex-row md:items-center md:justify-between">
              <p className="text-sm text-muted-foreground">
                {t('workforceReadiness.training.noOrgNotice')}
              </p>
              <Button variant="outline" asChild>
                <Link to="/settings/organizations">{t('workforceReadiness.training.goToOrganizations')}</Link>
              </Button>
            </CardContent>
          </Card>
        ) : null}

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{t('workforceReadiness.training.filters')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 lg:grid-cols-3">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(event) => {
                    setSearch(event.target.value);
                  }}
                  placeholder={t('workforceReadiness.training.searchPlaceholder')}
                  className="pl-9"
                />
              </div>
              <Select
                value={statusFilter}
                onChange={(value) => {
                  setStatusFilter(value as TrainingStatusFilter);
                }}
                options={[
                  { value: 'all', label: t('workforceReadiness.training.allStatuses') },
                  { value: 'planned', label: t('workforceReadiness.training.planned') },
                  { value: 'active', label: t('workforceReadiness.training.active') },
                  { value: 'completed', label: t('workforceReadiness.training.completed') },
                  { value: 'archived', label: t('workforceReadiness.training.archived') },
                ]}
              />
              <Select
                value={typeFilter}
                onChange={(value) => {
                  setTypeFilter(value);
                }}
                options={trainingTypeOptions.map((type) => ({
                  value: type,
                  label: type === 'all' ? t('workforceReadiness.training.allTrainingTypes') : type,
                }))}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('workforceReadiness.training.trainingName')}</TableHead>
                    <TableHead>{t('workforceReadiness.training.type')}</TableHead>
                    <TableHead>{t('workforceReadiness.training.status')}</TableHead>
                    <TableHead>{t('workforceReadiness.training.date')}</TableHead>
                    <TableHead>{t('workforceReadiness.training.assignedEmployees')}</TableHead>
                    <TableHead>{t('workforceReadiness.training.completionEvidence')}</TableHead>
                    <TableHead>{t('workforceReadiness.training.complianceControlsCol')}</TableHead>
                    <TableHead className="w-[200px]">{t('workforceReadiness.training.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRecords.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="py-6 text-center text-muted-foreground">
                        {t('workforceReadiness.training.noMatchingRecords')}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredRecords.map((training) => (
                      <TableRow key={training.id} className="align-top">
                        <TableCell className="min-w-[240px]">
                          <button
                            type="button"
                            onClick={() => {
                              openEditDialog(training);
                            }}
                            className="font-medium text-primary hover:underline text-left"
                          >
                            {training.trainingName}
                          </button>
                          <p className="mt-1 text-xs text-muted-foreground">
                            {training.description}
                          </p>
                        </TableCell>
                        <TableCell>{training.trainingType}</TableCell>
                        <TableCell>
                          <Badge variant={getTrainingStatusBadgeVariant(training.status)}>
                            {toLabel(training.status)}
                          </Badge>
                        </TableCell>
                        <TableCell>{formatDate(training.date)}</TableCell>
                        <TableCell className="min-w-[220px]">
                          {training.assignedEmployees.length === 0 ? (
                            <p className="text-sm text-muted-foreground">
                              {t('workforceReadiness.training.noParticipantsAssigned')}
                            </p>
                          ) : (
                            <div className="space-y-1.5">
                              {training.assignedEmployees.map((employee) => {
                                const member = membersByUserId.get(employee.userId);
                                const displayName = getMemberDisplayName(member, employee.userId);

                                if (member) {
                                  return (
                                    <Link
                                      key={`${training.id}-${employee.userId}`}
                                      to="/settings/team/members/$userId"
                                      params={{ userId: employee.userId }}
                                      className="block text-sm font-medium text-primary hover:underline"
                                    >
                                      {displayName}
                                    </Link>
                                  );
                                }

                                return (
                                  <p
                                    key={`${training.id}-${employee.userId}`}
                                    className="text-sm text-muted-foreground"
                                  >
                                    {displayName}
                                  </p>
                                );
                              })}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="min-w-[320px]">
                          {training.assignedEmployees.length === 0 ? (
                            <p className="text-sm text-muted-foreground">
                              {t('workforceReadiness.training.noParticipantProgress')}
                            </p>
                          ) : (
                            <div className="space-y-2">
                              {training.assignedEmployees.map((employee) => {
                                const member = membersByUserId.get(employee.userId);
                                const displayName = getMemberDisplayName(member, employee.userId);

                                return (
                                  <div
                                    key={`${training.id}-progress-${employee.userId}`}
                                    className="rounded-md border p-2 text-xs"
                                  >
                                    <div className="flex items-center justify-between gap-2">
                                      {member ? (
                                        <Link
                                          to="/settings/team/members/$userId"
                                          params={{ userId: employee.userId }}
                                          className="font-medium text-primary hover:underline"
                                        >
                                          {displayName}
                                        </Link>
                                      ) : (
                                        <span className="font-medium text-muted-foreground">
                                          {displayName}
                                        </span>
                                      )}
                                      <Badge
                                        variant={getCompletionStatusBadgeVariant(
                                          employee.completionStatus
                                        )}
                                      >
                                        {toLabel(employee.completionStatus)}
                                      </Badge>
                                    </div>
                                    <p className="mt-1 text-muted-foreground">
                                      {employee.completionDate
                                        ? t('workforceReadiness.training.completionDate', { date: formatDate(employee.completionDate) })
                                        : t('workforceReadiness.training.completionDatePending')}
                                    </p>
                                    <p className="mt-1 text-muted-foreground">
                                      {(training.evidenceCount ?? 0) > 0 ? (
                                        <Link
                                          to="/workforce-readiness/training-register/$trainingId"
                                          params={{ trainingId: training.id }}
                                          className="text-primary hover:underline"
                                        >
                                          {t('workforceReadiness.training.linkedEvidenceCount', { count: training.evidenceCount ?? 0 })}
                                        </Link>
                                      ) : (
                                        employee.evidenceLabel ?? t('workforceReadiness.training.noLinkedEvidence')
                                      )}
                                    </p>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="min-w-[240px]">
                          {training.linkedControlIds.length === 0 ? (
                            <p className="text-sm text-muted-foreground">{t('workforceReadiness.training.noControlsLinked')}</p>
                          ) : (
                            <div className="space-y-1.5">
                              {training.linkedControlIds.map((controlId) => {
                                const control = controlsById.get(controlId);
                                const label = getControlLabel(control, controlId);

                                return (
                                  <Link
                                    key={`${training.id}-${controlId}`}
                                    to="/controls/$controlId"
                                    params={{ controlId }}
                                    className="block text-sm text-primary hover:underline"
                                  >
                                    {label}
                                  </Link>
                                );
                              })}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                openEditDialog(training);
                              }}
                            >
                              {t('workforceReadiness.training.editAction')}
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                void navigate({
                                  to: '/audit/evidence-upload',
                                  search: {
                                    linkedObjectType: 'training_record',
                                    linkedObjectId: training.id,
                                    complianceContext: `Training: ${training.trainingName}`,
                                  },
                                });
                              }}
                            >
                              <FileUp className="mr-1 h-3.5 w-3.5" />
                              {t('workforceReadiness.training.evidenceAction')}
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>

      <Dialog
        open={dialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            closeDialog();
          } else {
            setDialogOpen(true);
          }
        }}
      >
        <DialogContent className="sm:max-w-5xl">
          <DialogHeader>
            <DialogTitle>{editingTrainingId ? t('workforceReadiness.training.editTrainingDialogTitle') : t('workforceReadiness.training.addTrainingDialogTitle')}</DialogTitle>
            <DialogDescription>
              {editingTrainingId
                ? t('workforceReadiness.training.editTrainingDialogDesc')
                : t('workforceReadiness.training.addTrainingDialogDesc')}
            </DialogDescription>
          </DialogHeader>

          <div className="max-h-[70vh] overflow-auto pr-1">
            <div className="space-y-5">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-1.5">
                  <p className="text-sm font-medium">{t('workforceReadiness.training.trainingNameLabel')}</p>
                  <Input
                    value={formState.trainingName}
                    onChange={(event) => {
                      setFormState((previousState) => ({
                        ...previousState,
                        trainingName: event.target.value,
                      }));
                    }}
                    placeholder={t('workforceReadiness.training.trainingNamePlaceholder')}
                  />
                </div>
                <div className="space-y-1.5">
                  <p className="text-sm font-medium">{t('workforceReadiness.training.typeOfTrainingLabel')}</p>
                  <Input
                    value={formState.trainingType}
                    onChange={(event) => {
                      setFormState((previousState) => ({
                        ...previousState,
                        trainingType: event.target.value,
                      }));
                    }}
                    placeholder={t('workforceReadiness.training.typeOfTrainingPlaceholder')}
                  />
                </div>
                <div className="space-y-1.5">
                  <p className="text-sm font-medium">{t('workforceReadiness.training.statusLabel')}</p>
                  <Select
                    value={formState.status}
                    onChange={(value) => {
                      setFormState((previousState) => ({
                        ...previousState,
                        status: value as TrainingRecordStatus,
                      }));
                    }}
                    options={[
                      { value: 'planned', label: t('workforceReadiness.training.planned') },
                      { value: 'active', label: t('workforceReadiness.training.active') },
                      { value: 'completed', label: t('workforceReadiness.training.completed') },
                      { value: 'archived', label: t('workforceReadiness.training.archived') },
                    ]}
                  />
                </div>
                <div className="space-y-1.5">
                  <p className="text-sm font-medium">{t('workforceReadiness.training.dateLabel')}</p>
                  <Input
                    type="date"
                    value={formState.date}
                    onChange={(event) => {
                      setFormState((previousState) => ({
                        ...previousState,
                        date: event.target.value,
                      }));
                    }}
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <p className="text-sm font-medium">{t('workforceReadiness.training.descriptionLabel')}</p>
                <Textarea
                  value={formState.description}
                  onChange={(event) => {
                    setFormState((previousState) => ({
                      ...previousState,
                      description: event.target.value,
                    }));
                  }}
                  placeholder={t('workforceReadiness.training.descriptionPlaceholder')}
                  rows={3}
                />
              </div>

              <div className="grid gap-4 xl:grid-cols-2">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">{t('workforceReadiness.training.participantsCard')}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <Input
                      value={participantSearch}
                      onChange={(event) => {
                        setParticipantSearch(event.target.value);
                      }}
                      placeholder={t('workforceReadiness.training.searchTeamMembers')}
                      disabled={!canManageAssignments}
                    />
                    <div className="max-h-56 space-y-2 overflow-auto rounded-md border p-2">
                      {!canManageAssignments ? (
                        <p className="text-sm text-muted-foreground">
                          {t('workforceReadiness.training.selectOrgToLoadTeam')}
                        </p>
                      ) : membersLoading ? (
                        <p className="text-sm text-muted-foreground">{t('workforceReadiness.training.loadingParticipants')}</p>
                      ) : filteredMembersForDialog.length === 0 ? (
                        <p className="text-sm text-muted-foreground">{t('workforceReadiness.training.noMatchingTeamMembers')}</p>
                      ) : (
                        filteredMembersForDialog.map((member) => {
                          const isChecked = formState.selectedParticipantIds.includes(
                            member.userId
                          );

                          return (
                            <label
                              key={member.userId}
                              className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 hover:bg-muted"
                            >
                              <Checkbox
                                checked={isChecked}
                                onCheckedChange={(checked) => {
                                  upsertParticipantSelection(member.userId, checked === true);
                                }}
                              />
                              <span className="text-sm">
                                {getMemberDisplayName(member)}
                                <span className="ml-1 text-xs text-muted-foreground">
                                  ({member.email})
                                </span>
                              </span>
                            </label>
                          );
                        })
                      )}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">{t('workforceReadiness.training.linkedControlsCard')}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <Input
                      value={controlSearch}
                      onChange={(event) => {
                        setControlSearch(event.target.value);
                      }}
                      placeholder={t('workforceReadiness.training.searchControls')}
                      disabled={!canManageAssignments}
                    />
                    <div className="max-h-56 space-y-2 overflow-auto rounded-md border p-2">
                      {!canManageAssignments ? (
                        <p className="text-sm text-muted-foreground">
                          {t('workforceReadiness.training.selectOrgToLoadControls')}
                        </p>
                      ) : controlsLoading ? (
                        <p className="text-sm text-muted-foreground">{t('workforceReadiness.training.loadingControls')}</p>
                      ) : filteredControlsForDialog.length === 0 ? (
                        <p className="text-sm text-muted-foreground">{t('workforceReadiness.training.noMatchingControls')}</p>
                      ) : (
                        filteredControlsForDialog.map((control) => {
                          const isChecked = formState.selectedControlIds.includes(control.id);

                          return (
                            <label
                              key={control.id}
                              className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 hover:bg-muted"
                            >
                              <Checkbox
                                checked={isChecked}
                                onCheckedChange={(checked) => {
                                  upsertControlSelection(control.id, checked === true);
                                }}
                              />
                              <span className="text-sm">
                                {getControlLabel(control, control.id)}
                              </span>
                            </label>
                          );
                        })
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">{t('workforceReadiness.training.participantCompletionCard')}</CardTitle>
                </CardHeader>
                <CardContent>
                  {formState.selectedParticipantIds.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      {t('workforceReadiness.training.selectParticipantsNotice')}
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {formState.selectedParticipantIds.map((userId) => {
                        const member = membersByUserId.get(userId);
                        const displayName = getMemberDisplayName(member, userId);
                        const progress = formState.participantProgress[userId] ?? {
                          completionStatus: 'not_started' as const,
                          completionDate: null,
                          evidenceLabel: null,
                        };

                        return (
                          <div key={userId} className="rounded-md border p-3">
                            <p className="mb-2 text-sm font-medium">{displayName}</p>
                            <div className="grid gap-2 md:grid-cols-3">
                              <Select
                                value={progress.completionStatus}
                                onChange={(value) => {
                                  updateParticipantProgress(userId, {
                                    completionStatus: value as EmployeeCompletionStatus,
                                  });
                                }}
                                options={[
                                  { value: 'not_started', label: t('workforceReadiness.training.notStarted') },
                                  { value: 'in_progress', label: t('workforceReadiness.training.inProgress') },
                                  { value: 'completed', label: t('workforceReadiness.training.completedStatus') },
                                ]}
                              />
                              <Input
                                type="date"
                                value={progress.completionDate ?? ''}
                                onChange={(event) => {
                                  updateParticipantProgress(userId, {
                                    completionDate: event.target.value || null,
                                  });
                                }}
                              />
                              <Input
                                value={progress.evidenceLabel ?? ''}
                                onChange={(event) => {
                                  updateParticipantProgress(userId, {
                                    evidenceLabel: event.target.value || null,
                                  });
                                }}
                                placeholder={t('workforceReadiness.training.certificatePlaceholder')}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>

              {formError ? (
                <p className="text-sm font-medium text-destructive">{formError}</p>
              ) : null}
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={closeDialog}
              disabled={createTrainingMutation.isCreating || updateTrainingMutation.isUpdating}
            >
              {t('workforceReadiness.training.cancel')}
            </Button>
            <Button
              onClick={() => {
                void saveTraining();
              }}
              disabled={createTrainingMutation.isCreating || updateTrainingMutation.isUpdating}
            >
              {createTrainingMutation.isCreating || updateTrainingMutation.isUpdating
                ? t('workforceReadiness.training.saving')
                : editingTrainingId
                  ? t('workforceReadiness.training.saveChanges')
                  : t('workforceReadiness.training.createTraining')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function uniqueValues(values: string[]): string[] {
  return Array.from(new Set(values));
}

function getTrainingStatusBadgeVariant(
  status: TrainingRecordStatus
): 'default' | 'secondary' | 'outline' | 'destructive' {
  if (status === 'active') {
    return 'default';
  }
  if (status === 'completed') {
    return 'secondary';
  }
  if (status === 'planned') {
    return 'outline';
  }
  return 'destructive';
}

function getCompletionStatusBadgeVariant(
  status: EmployeeCompletionStatus
): 'default' | 'secondary' | 'outline' | 'destructive' {
  if (status === 'completed') {
    return 'secondary';
  }
  if (status === 'in_progress') {
    return 'default';
  }
  return 'outline';
}

function getMemberDisplayName(
  member: OrganizationMember | undefined,
  fallbackUserId?: string
): string {
  if (!member) {
    return fallbackUserId ?? 'Unknown user';
  }

  if (member.displayName) {
    return member.displayName;
  }

  const firstName = member.firstName?.trim() ?? '';
  const lastName = member.lastName?.trim() ?? '';
  const fullName = `${firstName} ${lastName}`.trim();

  if (fullName) {
    return fullName;
  }

  return member.email;
}

function getControlLabel(control: Control | undefined, fallbackControlId: string): string {
  if (!control) {
    return fallbackControlId;
  }

  return `${control.controlCode} - ${control.title}`;
}

function toLabel(value: string): string {
  return value.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatDate(value: string): string {
  return new Date(value).toLocaleDateString();
}
